import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { supabase } from 'Services/supabaseClient'
import { InputText } from 'primereact/inputtext'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { InputSwitch } from 'primereact/inputswitch'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import { DialogModal } from 'Components/UIComponents'

const ManageInternalUsers = ({ setLoading }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const location = useLocation()
  const communityData = location?.state?.communityData

  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [userData, setUserData] = useState([])
  const [searchedValue, setSearchedValue] = useState('')
  const [first, setFirst] = useState(0)

  useEffect(() => {
    fetchCommunityMembers()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const fetchCommunityMembers = async () => {
    setShowDialog({ ...showDialog, showModal: false })

    setLoading(true)
    try {
      const { data: internalUsers, error: internalUsersError } = await supabase.from('users').select('first_name, email, phone, userid').eq('is_internal_user', true)

      if (internalUsersError) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_DATA_FOUND'),
          onRetry: () => fetchCommunityMembers(),
          onHide: handleDismiss
        })
      }

      const communityId = communityData?.communityId

      const { data: communityMembers, error: communityMembersError } = await supabase.from('community_members').select('userid').eq('community_id', communityId)

      if (communityMembersError) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('NO_DATA_FOUND'),
          onRetry: () => fetchCommunityMembers(),
          onHide: handleDismiss
        })
      }

      const memberIdsSet = new Set(communityMembers.map((member) => member.userid))

      const internalUsersWithFlag = internalUsers.map((user) => ({
        ...user,
        isInCommunity: memberIdsSet.has(user.userid)
      }))

      setUserData(internalUsersWithFlag)
    } catch (err) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleCommunityMembership = async (user) => {
    setShowDialog({ ...showDialog, showModal: false })

    setLoading(true)

    try {
      if (user.isInCommunity) {
        const { status, error, data } = await supabase.from('community_members').delete().eq('userid', user.userid).eq('community_id', communityData.communityId).select('*').single()

        if (error) {
          setShowDialog({
            ...showDialog,
            showModal: true,
            modalType: 'error',
            message: t('FAILED_TO_REMOVE_COMMUNITY_USER'),
            onRetry: () => handleToggleCommunityMembership(user),
            onHide: handleDismiss
          })
        }

        if (status && data.userid) {
          setShowDialog({
            ...showDialog,
            showModal: true,
            modalType: 'success',
            message: t('USER_REMOVED_SUCCESSFULLY'),
            onHide: handleDismiss,
            onSuccess: handleDismiss
          })
        }

        setUserData((prevData) => {
          const updatedData = prevData.map((u) => (u.userid === user.userid ? { ...u, isInCommunity: !u.isInCommunity } : u))

          const searched = getFilteredSortedData(updatedData)

          const updatedUserIndex = searched.findIndex((u) => u.userid === user.userid)

          const newPage = Math.floor(updatedUserIndex / rowCount)

          setFirst(newPage * rowCount)

          return updatedData
        })
      } else {
        const { status, error, data } = await supabase
          .from('community_members')
          .insert([
            {
              userid: user.userid,
              community_id: communityData.communityId
            }
          ])
          .select('*')
          .single()

        if (error) {
          setShowDialog({
            ...showDialog,
            showModal: true,
            modalType: 'error',
            message: t('FAILED_TO_ADD_COMMUNITY_USER'),
            onRetry: () => handleToggleCommunityMembership(user),
            onHide: handleDismiss
          })
        }

        if (status === 201 && data.userid) {
          setShowDialog({
            ...showDialog,
            showModal: true,
            modalType: 'success',
            message: t('USER_ADDED_TO_COMMUNITY_SUCCESS'),
            onHide: handleDismiss,
            onSuccess: handleDismiss
          })
        }

        setUserData((prevData) => {
          const updatedData = prevData.map((u) => (u.userid === user.userid ? { ...u, isInCommunity: !u.isInCommunity } : u))

          const searched = getFilteredSortedData(updatedData)

          const updatedUserIndex = searched.findIndex((u) => u.userid === user.userid)

          const newPage = Math.floor(updatedUserIndex / rowCount)

          setFirst(newPage * rowCount)

          return updatedData
        })
      }
    } catch (error) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchInputChange = (value) => {
    setSearchedValue(value)
    setFirst(0)
  }

  const getFilteredSortedData = (data) =>
    data
      .filter((item) => item?.first_name?.toLowerCase()?.includes(searchedValue?.toLowerCase()) || item?.userid?.toString()?.includes(searchedValue) || item?.email?.toLowerCase()?.includes(searchedValue?.toLowerCase()) || item?.phone?.toString()?.includes(searchedValue))
      .sort((a, b) => {
        if (a.isInCommunity === b.isInCommunity) return 0

        return a.isInCommunity ? -1 : 1
      })

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleToggleSwitch = (rowData) => {
    const confirmMessage = rowData.isInCommunity === true ? t('COMMUNITY_USER_REMOVAL_CONFIRMATION') : t('COMMUNITY_USER_ADDING_CONFIRMATION')

    setShowDialog({
      ...showDialog,
      showModal: true,
      modalType: 'info',
      message: confirmMessage,
      onConfirmation: () => handleToggleCommunityMembership(rowData),
      onHide: handleDismiss
    })
  }

  const renderToggleSwitch = (rowData) => <InputSwitch checked={rowData?.isInCommunity} onChange={() => handleToggleSwitch(rowData)} />

  const manageUsersColumns = [
    {
      dataField: 'userid',
      text: t('USER_ID'),
      className: 'column-user-id',
      sortable: true
    },
    {
      dataField: 'first_name',
      text: t('NAME'),
      className: 'column-user-name',
      sortable: true
    },

    {
      dataField: 'email',
      text: t('EMAIL'),
      className: 'column-user-email',
      sortable: false
    },

    {
      dataField: 'phone',
      text: t('PHONE'),
      className: 'column-user-phone',
      sortable: false
    },

    {
      dataField: 'isInCommunity',
      text: t('IS_COMMUNITY_MEMBER'),
      className: 'column-in-community',
      sortable: false,
      body: renderToggleSwitch
    }
  ]

  const filteredUserData = userData
    ?.filter((item) => item?.first_name?.toLowerCase()?.includes(searchedValue?.toLowerCase()) || item?.userid?.toString()?.includes(searchedValue) || item?.email?.toLowerCase()?.includes(searchedValue?.toLowerCase()) || item?.phone?.toString()?.includes(searchedValue))
    ?.sort((a, b) => {
      if (a.isInCommunity === b.isInCommunity) return 0

      return a.isInCommunity ? -1 : 1
    })

  return (
    <LayoutContainer title={`${t('MANAGE_INTERNAL_USERS')} - ${communityData.communityName?.toUpperCase()}`} breadCrumbsTitle={'manageInternalCommunities'}>
      <div className="manage-internal-users-wrapper">
        <div className="custom-search-wrapper p-input-icon-left">
          <i className="custom-search-icon pi pi-search" />
          <InputText value={searchedValue} placeholder={t('SEARCH')} className="custom-search-input" onChange={(e) => handleSearchInputChange(e.target.value)} />
        </div>

        <div className="user-data-table">
          <DataTable value={filteredUserData} paginator={userData?.length} rows={rowCount} pageLinkSize={paginationButtons} first={first} onPage={(e) => setFirst(e.first)}>
            {manageUsersColumns?.map((column, index) => (
              <Column key={index} field={column.dataField} header={column.text} body={column.body} className={column.className} sortable={column.sortable} />
            ))}
          </DataTable>
        </div>

        <DialogModal {...showDialog} onDismiss={handleDismiss} />
      </div>
    </LayoutContainer>
  )
}

export default Loader(ManageInternalUsers)
